<template>
  <div class="map-picker">
    <!-- 地址搜索 -->
    <el-row :gutter="20" style="margin-bottom: 20px">
      <el-col :span="24">
        <el-input
                v-model="searchAddress"
                placeholder="输入地址进行搜索"
                id="search-input"
                ref="searchInput"
                @keyup.enter="handleSearch"
        >
          <template #append>
            <el-button icon="el-icon-search" @click="handleSearch" />
          </template>
        </el-input>
      </el-col>
    </el-row>

    <!-- 坐标输入 -->
    <el-row :gutter="20">
      <el-col :span="12">
        <el-input
                v-model="lng"
                placeholder="经度"
                @blur="handleCoordinateChange"
                @keyup.enter="handleCoordinateChange"
        >
          <template #prepend>经度</template>
        </el-input>
      </el-col>
      <el-col :span="12">
        <el-input
                v-model="lat"
                placeholder="纬度"
                @blur="handleCoordinateChange"
                @keyup.enter="handleCoordinateChange"
        >
          <template #prepend>纬度</template>
        </el-input>
      </el-col>
    </el-row>

    <!-- 操作按钮 -->
    <el-row :gutter="20" style="margin-top: 10px">
      <el-col :span="24">
        <el-button
                type="primary"
                size="small"
                @click="handleCoordinateChange"
                :disabled="!isValidCoordinates"
        >
          应用坐标
        </el-button>
        <el-button
                size="small"
                @click="getCurrentLocation"
        >
          获取当前位置
        </el-button>
      </el-col>
    </el-row>

    <!-- 地图容器 - 使用 ref -->
    <div ref="mapContainer" style="height: 400px; margin-top: 20px"></div>
  </div>
</template>

<script>
  import AMapLoader from '@amap/amap-jsapi-loader'

  export default {
    props: {
      modelValue: {
        type: Object,
        default: () => ({ lng: 118.341854, lat: 35.077435 })
      },
      formData: {
        type: Object,
        default: null,
      },
    },

    data() {
      return {
        map: null,
        marker: null,
        lng: this.modelValue.lng,
        lat: this.modelValue.lat,
        searchAddress: '',
        AMap: null,
        autoComplete: null,
        geocoder: null,
        geolocation: null,
        isManualInput: false // 标记是否是手动输入
      }
    },

    computed: {
      // 验证坐标是否有效
      isValidCoordinates() {
        const lng = parseFloat(this.lng)
        const lat = parseFloat(this.lat)
        return !isNaN(lng) && !isNaN(lat) &&
                lng >= -180 && lng <= 180 &&
                lat >= -90 && lat <= 90
      }
    },

    watch: {
      modelValue: {
        handler(newVal) {
          if (newVal && newVal.lng !== this.lng && newVal.lat !== this.lat) {
            console.log('父组件更新坐标:', newVal)
            this.lng = newVal.lng
            this.lat = newVal.lat
            // 如果不是手动输入触发的更新，则更新地图位置
            if (!this.isManualInput) {
              this.updatePosition({lng: newVal.lng, lat: newVal.lat})
            }
            this.isManualInput = false
          }
        },
        deep: true,
        immediate: true
      }
    },

    mounted() {
      this.initMap()
    },

    methods: {
      async initMap() {
        try {
          // 确保 DOM 已经渲染
          await this.$nextTick()

          // 检查地图容器是否存在
          if (!this.$refs.mapContainer) {
            console.error('地图容器未找到')
            return
          }

          this.AMap = await AMapLoader.load({
            key: 'a50f6a3b0f5058fa09bfbf6ad5acb4e3',
            version: '2.0',
            plugins: ['AMap.Geocoder', 'AMap.AutoComplete', 'AMap.PlaceSearch', 'AMap.Geolocation']
          })

          // 使用 ref 初始化地图
          this.map = new this.AMap.Map(this.$refs.mapContainer, {
            zoom: 13,
            center: [this.lng, this.lat]
          })

          // 初始化地理编码器
          this.geocoder = new this.AMap.Geocoder({
            city: '全国',
            batch: false
          })

          // 初始化定位插件
          this.geolocation = new this.AMap.Geolocation({
            enableHighAccuracy: true,
            timeout: 10000,
            buttonOffset: new this.AMap.Pixel(10, 20),
            zoomToAccuracy: true,
            buttonPosition: 'RB'
          })
          this.map.addControl(this.geolocation)

          // 确保搜索输入框已渲染
          await this.$nextTick()
          const searchInputEl = this.$refs.searchInput?.$el?.querySelector('input')
          if (searchInputEl) {
            this.autoComplete = new this.AMap.AutoComplete({
              input: searchInputEl
            })
            this.autoComplete.on('select', (e) => {
              this.handlePlaceSelect(e.poi)
            })
          }

          // 初始化标记
          this.addMarker([this.lng, this.lat])

          // 点击地图事件
          this.map.on('click', (e) => {
            this.updatePosition(e.lnglat)
          })

        } catch (error) {
          console.error('地图加载失败:', error)
        }
      },

      // 处理坐标输入变化
      handleCoordinateChange() {
        if (!this.isValidCoordinates) {
          this.$message.warning('请输入有效的经纬度坐标')
          return
        }

        const lng = parseFloat(this.lng)
        const lat = parseFloat(this.lat)

        this.isManualInput = true
        this.updatePosition({
          lng: lng,
          lat: lat
        })
      },

      // 获取当前位置
      getCurrentLocation() {
        if (!this.geolocation) {
          this.$message.warning('定位功能未初始化')
          return
        }

        this.geolocation.getCurrentPosition((status, result) => {
          if (status === 'complete') {
            this.$message.success('定位成功')
            this.updatePosition({
              lng: result.position.lng,
              lat: result.position.lat
            })

            // 逆地理编码获取地址
            this.geocoder.getAddress([result.position.lng, result.position.lat], (status, result) => {
              if (status === 'complete' && result.regeocode) {
                this.searchAddress = result.regeocode.formattedAddress
              }
            })
          } else {
            this.$message.error('定位失败：' + result.message)
            console.error('定位失败:', result)
          }
        })
      },

      // 地址搜索
      handleSearch() {
        if (!this.searchAddress.trim()) {
          this.$message.warning('请输入有效地址')
          return
        }

        this.geocoder.getLocation(this.searchAddress, (status, result) => {
          console.log('解析结果:' + status, result)
          if (status === 'complete' && result.geocodes.length > 0) {
            const bestMatch = result.geocodes[0]
            console.log('解析结果:', bestMatch)

            this.searchAddress = bestMatch.formattedAddress
            this.updatePosition({
              lng: bestMatch.location.lng,
              lat: bestMatch.location.lat
            })
          } else {
            this.$message.error('地址解析失败，请检查输入')
            console.error('地理编码错误:', result)
          }
        })
      },

      handlePlaceSelect(poi) {
        if (!poi.location) return
        this.searchAddress = poi.name
        this.updatePosition({
          lng: poi.location.lng,
          lat: poi.location.lat
        })
      },

      // 更新坐标位置
      updatePosition(lnglat) {
        this.lng = lnglat.lng.toFixed(6)
        this.lat = lnglat.lat.toFixed(6)
        this.addMarker([this.lng, this.lat])
        this.emitUpdate()
        this.map.setCenter([this.lng, this.lat])

        // 逆地理编码获取地址（可选）
        this.reverseGeocode(lnglat)
      },

      // 逆地理编码
      reverseGeocode(lnglat) {
        this.geocoder.getAddress([lnglat.lng, lnglat.lat], (status, result) => {
          if (status === 'complete' && result.regeocode) {
            this.searchAddress = result.regeocode.formattedAddress
          }
        })
      },

      addMarker(position) {
        if (this.marker) {
          this.map.remove(this.marker)
        }
        this.marker = new this.AMap.Marker({
          position: position,
          map: this.map
        })
      },

      emitUpdate() {
        console.log('坐标更新:', this.lng, this.lat)
        this.$emit('update', {
          lng: parseFloat(this.lng),
          lat: parseFloat(this.lat)
        })
        this.$emit('update:modelValue', {
          lng: parseFloat(this.lng),
          lat: parseFloat(this.lat)
        })
      },
    },

    beforeDestroy() {
      // 清理资源
      if (this.map) {
        this.map.destroy()
      }
    }
  }
</script>

<style scoped>
  .map-picker {
    width: 100%;
  }

  .el-button {
    margin-right: 10px;
  }

  .el-button:last-child {
    margin-right: 0;
  }
</style>
